// #region Imports
import KeyboardTabIcon from "@mui/icons-material/KeyboardTab";
import MuiDrawer from "@mui/material/Drawer";
import IconButton from "@mui/material/IconButton";
import { styled } from "@mui/material/styles";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import EditFeeScheduleCategory from "_shared/fee-schedules/categories/_views/edit-fee-schedule-category";
import FeeScheduleCategory from "_shared/fee-schedules/categories/fee-schedule-category";
import FeeScheduleCategoryName from "_shared/fee-schedules/categories/names/fee-schedule-category-name";
import { FeeScheduleCategoryMaskedCurrency } from "work/_shared/masked-fields";
// #endregion

// #region Styled Components
const Drawer = styled(MuiDrawer)(() => ({
  flexShrink: 0,
  '& .MuiDrawer-paper': {
    marginTop: '64px',
    width: '400px',
    boxSizing: 'border-box',
  },
}));
const FeeCategoryPanel = styled(Drawer, {
  shouldForwardProp: (prop) => prop !== "closeAfterTransition"
})<{ open?: boolean }>(({ theme, open }) => ({
  "& .MuiDrawer-paper": {
    width: open ? "400px" : 0,
    overflowX: "hidden",
    transition: theme.transitions.create("width", {
      easing: open
        ? theme.transitions.easing.easeOut
        : theme.transitions.easing.sharp,
      duration: open
        ? theme.transitions.duration.enteringScreen
        : theme.transitions.duration.leavingScreen,
    }),
  },
}));
const FeeCategoryPanelContainer = styled("div")(() => ({
  flex: 1,
  height: "100%",
}));
const FeeCategoryPanelContent = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  height: "100%",
  padding: theme.spacing(2),
  width: "400px",
}));
const TitleBar = styled("div")(({ theme }) => ({
  alignItems: "center",
  display: "flex",
  justifyContent: "space-between",
  flexDirection: "row",
  paddingBottom: theme.spacing(1),
}));
// #endregion

// #region Props/Types
type EditFeeScheduleCategoryDrawerProps = {
  category?: FeeScheduleCategory | FeeScheduleCategoryMaskedCurrency;
  isOpen: boolean;
  onOpen: () => void;
  onClose: () => void;
  onCategoryAdded?: (category: FeeScheduleCategory | FeeScheduleCategoryMaskedCurrency) => void;
  onCategoryUpdated?: (
    originalName: FeeScheduleCategoryName | null,
    category: FeeScheduleCategory | FeeScheduleCategoryMaskedCurrency,
  ) => void;
};
// #endregion

export default function EditFeeScheduleCategoryDrawer(props: Readonly<EditFeeScheduleCategoryDrawerProps>) {
  const {
    category,
    isOpen,
    onClose,
    onCategoryAdded,
    onCategoryUpdated,
  } = props;

  // #region Render
  return (
    <FeeCategoryPanel open={isOpen} anchor="right" variant="persistent" closeAfterTransition={false}>
      <FeeCategoryPanelContainer>
        <FeeCategoryPanelContent>
          <TitleBar>
            <Typography variant="h5">{category ? "Edit Category" : "Create New Category"}</Typography>
            <Tooltip title="Minimize panel">
              <IconButton size="medium" onClick={onClose}>
                <KeyboardTabIcon fontSize="large" />
              </IconButton>
            </Tooltip>
          </TitleBar>
          <EditFeeScheduleCategory
            key={category?.id.value ?? "new"}
            category={category}
            onCategoryAdded={onCategoryAdded}
            onCategoryUpdated={onCategoryUpdated}
          />
        </FeeCategoryPanelContent>
      </FeeCategoryPanelContainer>
    </FeeCategoryPanel>
  );
  // #endregion
}
